// Phase 0091 — HR Automation Control Dashboard.
// One page where management sees whether HR automation is actually
// running: scheduler health, queue depths, last-run times, and the
// four manual trigger buttons.

import {
  App, Alert, Button, Card, Col, Empty, List, Row, Space, Statistic,
  Tag, Tooltip, Typography,
} from 'antd'
import {
  CheckCircleOutlined, ClockCircleOutlined, ReloadOutlined,
  ThunderboltOutlined, WarningOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { hrAutomationApi, schedulerApi } from '@/features/hr/schedulerApi'
import PageHeader from '@/shared/components/PageHeader'

dayjs.extend(relativeTime)

const { Text, Paragraph } = Typography

// Threshold colour map for a KPI count
function thresholdColor(value: number, warning: number, danger: number,
                        lowerIsBetter = true): string {
  if (lowerIsBetter) {
    if (value >= danger) return '#cf1322'
    if (value >= warning) return '#fa8c16'
    return '#52c41a'
  }
  // Higher-is-better
  if (value <= 0) return '#cf1322'
  return '#52c41a'
}

export default function HRAutomationDashboardPage() {
  const { message: msg } = App.useApp()
  const navigate = useNavigate()

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['hr-automation-dashboard'],
    queryFn: () => hrAutomationApi.dashboard(),
    refetchInterval: 30_000,
  })

  // Quick-trigger mutations for the four key jobs
  const triggerJob = useMutation({
    mutationFn: (code: string) => schedulerApi.trigger(code),
    onSuccess: (data: unknown, code) => {
      const d = data as { status?: string; duration_ms?: number }
      msg.success(`${code}: ${d.status ?? 'OK'} in ${d.duration_ms ?? 0}ms`)
      refetch()
    },
    onError: (e: Error) => msg.error(e.message),
  })

  const deriveAttendance = useMutation({
    mutationFn: () => hrAutomationApi.deriveAttendance({}),
    onSuccess: (data) => {
      msg.success(
        `Derivation: ${data.rows_created} created, ${data.rows_updated} updated, ` +
        `${data.rows_flagged_review} need review`)
      refetch()
    },
    onError: (e: Error) => msg.error(e.message),
  })

  const kpis = data?.kpis
  const lastTaskRun = kpis?.last_task_generation_run
  const lastEscRun = kpis?.last_escalation_sweep

  return (
    <>
      <PageHeader
        title="HR Automation Control"
        subtitle="Live KPI surface for the whole HR automation pipeline. Refreshes every 30 seconds."
        extra={
          <Space>
            <Text type="secondary">
              as of {data?.as_of ? dayjs(data.as_of).format('HH:mm:ss') : '—'}
            </Text>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Refresh
            </Button>
          </Space>
        }
      />

      {/* Top scheduler health strip */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card size="small" hoverable
                onClick={() => navigate('/hr/scheduler')}>
            <Statistic
              title="Scheduler jobs active"
              value={kpis?.scheduler_jobs_active ?? 0}
              prefix={<ThunderboltOutlined />}
              valueStyle={{
                color: thresholdColor(kpis?.scheduler_jobs_active ?? 0, 0, 0, false),
              }}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small">
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
              <Text type="secondary">Last task generation</Text>
              {lastTaskRun?.started_at ? (
                <>
                  <Space size={8}>
                    <Tag color={lastTaskRun.status === 'SUCCESS' ? 'green'
                                 : lastTaskRun.status === 'FAILED' ? 'red' : 'default'}>
                      {lastTaskRun.status}
                    </Tag>
                    <Text strong>{dayjs(lastTaskRun.started_at).fromNow()}</Text>
                  </Space>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {lastTaskRun.rows_affected ?? '—'} rows in {lastTaskRun.duration_ms}ms
                  </Text>
                </>
              ) : <Text type="secondary">Never run</Text>}
              <Button size="small" type="primary"
                      loading={triggerJob.isPending}
                      onClick={() => triggerJob.mutate('JOB_DAILY_TASK_GENERATION')}>
                Run now
              </Button>
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small">
            <Space direction="vertical" style={{ width: '100%' }} size={4}>
              <Text type="secondary">Last escalation sweep</Text>
              {lastEscRun?.started_at ? (
                <>
                  <Space size={8}>
                    <Tag color={lastEscRun.status === 'SUCCESS' ? 'green'
                                 : lastEscRun.status === 'FAILED' ? 'red' : 'default'}>
                      {lastEscRun.status}
                    </Tag>
                    <Text strong>{dayjs(lastEscRun.started_at).fromNow()}</Text>
                  </Space>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {lastEscRun.duration_ms}ms
                  </Text>
                </>
              ) : <Text type="secondary">Never run</Text>}
              <Button size="small" type="primary"
                      loading={triggerJob.isPending}
                      onClick={() => triggerJob.mutate('JOB_ESCALATION_SWEEP')}>
                Run now
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Queue depth KPIs */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small" hoverable
                onClick={() => navigate('/hr/escalation-inbox')}>
            <Statistic
              title="Open escalations"
              value={kpis?.open_escalations ?? 0}
              valueStyle={{
                color: thresholdColor(kpis?.open_escalations ?? 0, 5, 20),
              }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic
              title="Notifications pending"
              value={kpis?.notifications_pending ?? 0}
              valueStyle={{
                color: thresholdColor(kpis?.notifications_pending ?? 0, 10, 50),
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Tooltip title="Employees with punches today but no derived attendance row">
            <Card size="small" hoverable
                  onClick={() => navigate('/hr/attendance-derivation')}>
              <Statistic
                title="Attendance derivation pending"
                value={kpis?.attendance_derivation_pending ?? 0}
                valueStyle={{
                  color: thresholdColor(kpis?.attendance_derivation_pending ?? 0, 5, 20),
                }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic
              title="Missing punch (today)"
              value={kpis?.missing_punch_count_today ?? 0}
              valueStyle={{
                color: thresholdColor(kpis?.missing_punch_count_today ?? 0, 5, 20),
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Tooltip title="Active responsibility lines with no task template">
            <Card size="small" hoverable
                  onClick={() => navigate('/hr/role-master')}>
              <Statistic
                title="Task templates missing"
                value={kpis?.task_templates_missing ?? 0}
                valueStyle={{
                  color: thresholdColor(kpis?.task_templates_missing ?? 0, 5, 20),
                }}
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small" hoverable
                onClick={() => navigate('/hr/certification-expiry')}>
            <Statistic
              title="Certs expiring (30d)"
              value={kpis?.certifications_expiring_30d ?? 0}
              valueStyle={{
                color: thresholdColor(kpis?.certifications_expiring_30d ?? 0, 5, 15),
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic
              title="Certs expired"
              value={kpis?.certifications_expired ?? 0}
              valueStyle={{
                color: thresholdColor(kpis?.certifications_expired ?? 0, 1, 10),
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic
              title="Daily scores pending"
              value={kpis?.daily_scores_pending ?? 0}
              valueStyle={{
                color: thresholdColor(kpis?.daily_scores_pending ?? 0, 50, 200),
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic
              title="Monthly score pending"
              value={kpis?.monthly_scores_pending ?? 0}
              valueStyle={{
                color: thresholdColor(kpis?.monthly_scores_pending ?? 0, 0, 1),
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Tooltip title="Today's responsibility task instances tagged with a study">
            <Card size="small" hoverable
                  onClick={() => navigate('/hr/live-studies')}>
              <Statistic
                title="Study tasks today"
                value={kpis?.study_tasks_generated ?? 0}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Tooltip>
        </Col>
      </Row>

      {/* Quick-action strip */}
      <Card size="small" title="Quick actions" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Button onClick={() => navigate('/hr/attendance-derivation')}>
            Derive attendance from punches…
          </Button>
          <Button onClick={() => navigate('/hr/certification-expiry')}>
            Open certification expiry report
          </Button>
          <Button onClick={() => deriveAttendance.mutate()}
                  loading={deriveAttendance.isPending}>
            Quick-derive attendance for today
          </Button>
          <Button onClick={() => triggerJob.mutate('JOB_CERTIFICATION_EXPIRY')}
                  loading={triggerJob.isPending}>
            Run certification expiry check
          </Button>
          <Button onClick={() => triggerJob.mutate('JOB_DAILY_SCORE_COMPUTE')}
                  loading={triggerJob.isPending}>
            Compute today's scores
          </Button>
        </Space>
      </Card>

      {/* Recent scheduler failures */}
      {data?.recent_failures && data.recent_failures.length > 0 && (
        <Card size="small"
              title={<Space><WarningOutlined style={{ color: '#cf1322' }} />
                <Text strong>Recent scheduler failures (last 24h)</Text></Space>}
              style={{ marginBottom: 16 }}>
          <List
            size="small"
            dataSource={data.recent_failures}
            renderItem={(f) => (
              <List.Item>
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                  <Space>
                    <Tag color="red">FAILED</Tag>
                    <Text code style={{ fontSize: 11 }}>{f.job_code}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {f.started_at ? dayjs(f.started_at).fromNow() : '—'}
                    </Text>
                  </Space>
                  {f.error_text && (
                    <Paragraph type="danger" style={{ marginBottom: 0, fontSize: 12 }}>
                      {f.error_text}
                    </Paragraph>
                  )}
                </Space>
              </List.Item>
            )}
          />
        </Card>
      )}

      {!isLoading && !data && <Empty description="No data" />}
    </>
  )
}
